import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";

// Module-scoped guard: protects against React StrictMode's intentional
// double-invoke of effects in development, which would otherwise consume
// the token on the first call and surface a spurious "already used" error
// on the second.
const attemptedTokens = new Set();

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setUser } = useAuth();

  // verifying | success | error
  const [status, setStatus] = useState("verifying");
  const [errorMessage, setErrorMessage] = useState("");
  const [verifiedUser, setVerifiedUser] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMessage("This link is missing its verification token. Please use the full link from the email.");
      return;
    }
    if (attemptedTokens.has(token)) return;
    attemptedTokens.add(token);

    api
      .post("/auth/verify-email", { token })
      .then((res) => {
        setStatus("success");
        setVerifiedUser(res.data);
        // Hydrate the auth context so subsequent navigations are signed-in.
        // The session cookie is already set by the backend on this response.
        if (res.data && res.data.id) {
          setUser(res.data);
        }
      })
      .catch((err) => {
        setStatus("error");
        setErrorMessage(
          formatApiErrorDetail(err.response?.data?.detail) ||
            "We couldn't verify this link. It may have expired or already been used."
        );
      });
  }, [token, setUser]);

  const continueToApp = () => {
    if (!verifiedUser) {
      navigate("/login");
      return;
    }
    // Immediately-post-signup landing:
    //   * Filmmaker → Edit Profile so they can fill out bio / roles / bank
    //     details before their first upload. New signups verify their email
    //     to activate, so this verify-success handler doubles as the
    //     "just finished signing up" moment for filmmakers.
    //   * Viewer   → /browse to start exploring.
    //   * Admin    → /admin panel.
    const target =
      verifiedUser.role === "filmmaker"
        ? "/filmmaker/profile"
        : verifiedUser.role === "admin"
        ? "/admin"
        : "/browse";
    navigate(target);
  };

  return (
    <div className="min-h-screen bg-ink grain flex items-center justify-center px-4 py-16">
      <div className="absolute top-6 left-6">
        <Link to="/" className="flex items-center group">
          <Logo size={32} />
        </Link>
      </div>

      <div
        className="w-full max-w-md bg-surface border border-white/10 rounded-2xl p-8 sm:p-10 shadow-2xl shadow-terracotta/5 text-center"
        data-testid="verify-email-card"
      >
        {status === "verifying" && (
          <div className="space-y-4" data-testid="verify-status-loading">
            <Loader2 className="w-12 h-12 text-terracotta animate-spin mx-auto" />
            <h1 className="font-display text-2xl font-black">Confirming your email…</h1>
            <p className="text-foreground/60 text-sm">Hang tight, this only takes a second.</p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-5" data-testid="verify-status-success">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-9 h-9 text-emerald-400" />
            </div>
            <h1 className="font-display text-2xl font-black">You're in</h1>
            <p className="text-foreground/70 text-sm">
              Welcome{verifiedUser?.name ? `, ${verifiedUser.name}` : ""}. Your email is confirmed and your
              Cinevoya account is active.
            </p>
            <Button
              onClick={continueToApp}
              className="w-full bg-terracotta hover:bg-ember text-white h-11"
              data-testid="verify-continue-btn"
            >
              {verifiedUser?.role === "filmmaker"
                ? "Complete your profile"
                : verifiedUser?.role === "admin"
                ? "Open admin"
                : "Start watching"}
            </Button>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-5" data-testid="verify-status-error">
            <div className="w-16 h-16 rounded-full bg-terracotta/15 border border-terracotta/40 flex items-center justify-center mx-auto">
              <XCircle className="w-9 h-9 text-terracotta" />
            </div>
            <h1 className="font-display text-2xl font-black">Couldn't verify</h1>
            <p className="text-foreground/70 text-sm">{errorMessage}</p>
            <div className="space-y-2">
              <Button
                onClick={() => navigate("/login")}
                variant="outline"
                className="w-full border-white/15 bg-transparent hover:bg-white/5 h-11"
                data-testid="verify-go-to-login-btn"
              >
                Go to sign in
              </Button>
              <p className="text-xs text-foreground/50">
                Need a new link? Try signing in and use the "Resend confirmation" option.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
